import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { SearchIcon, UsersIcon, XIcon } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { boolean, number, object, optional, picklist, string } from 'valibot';
import { useGetSubscription } from '@/api/subscription/queries';
import { getUsersOptions } from '@/api/users/queries';
import type { GetUsersResponseDto } from '@/api/users/types';
import { EmptyPlaceholder } from '@/components/empty-placeholder';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { ProTierPreview } from '@/components/pro-tier-preview';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { UpgradeLink } from '@/components/upgrade-link';
import { UsersDataTable } from './-components/users-table';
import { mockData } from './-components/users-table/config';

type MetaOperator = 'eq' | 'contains' | 'exists';

const queryParamsSchema = object({
  direction: optional(picklist(['next', 'prev']), 'next'),
  cursor: optional(string(), ''),
  pageSize: optional(number(), 10),
  withEmail: optional(boolean(), false),
  metaKey: optional(string(), ''),
  metaOperator: optional(picklist(['eq', 'contains', 'exists']), 'contains'),
  metaValue: optional(string(), ''),
});

const buildUsersQuery = (search: {
  direction: 'next' | 'prev';
  cursor: string;
  pageSize: number;
  withEmail: boolean;
  metaKey: string;
  metaOperator: MetaOperator;
  metaValue: string;
}) => {
  const query: Record<string, string | number> = {
    direction: search.direction,
    cursor: search.cursor,
    limit: search.pageSize,
    withEmail: String(search.withEmail),
  };
  const metaKey = search.metaKey.trim();

  if (metaKey) {
    query.metaKey = metaKey;
    query.metaOperator = search.metaOperator;

    if (search.metaOperator !== 'exists') {
      query.metaValue = search.metaValue.trim();
    }
  }

  return query;
};

export const Route = createFileRoute('/_authenticated/_projects/projects/$projectId/users/')({
  component: UserComponent,
  validateSearch: queryParamsSchema,
  loaderDeps: ({
    search: { direction, cursor, pageSize, withEmail, metaKey, metaOperator, metaValue },
  }) => ({
    direction,
    cursor,
    pageSize,
    withEmail,
    metaKey,
    metaOperator,
    metaValue,
  }),
  loader: async ({ context: { queryClient }, params: { projectId }, deps }) => {
    await queryClient.ensureQueryData(getUsersOptions(projectId, buildUsersQuery(deps)));
  },
});

function UserComponent() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const { data: subscription } = useGetSubscription();
  const { data } = useSuspenseQuery(getUsersOptions(projectId, buildUsersQuery(search)));
  const [metaKey, setMetaKey] = useState(search.metaKey);
  const [metaOperator, setMetaOperator] = useState<MetaOperator>(search.metaOperator);
  const [metaValue, setMetaValue] = useState(search.metaValue);

  const isFreePlan = subscription?.isFree;
  const hasMetaFilter = Boolean(search.metaKey.trim());
  const canApplyMetaFilter =
    Boolean(metaKey.trim()) && (metaOperator === 'exists' || Boolean(metaValue.trim()));

  useEffect(() => {
    setMetaKey(search.metaKey);
    setMetaOperator(search.metaOperator);
    setMetaValue(search.metaValue);
  }, [search.metaKey, search.metaOperator, search.metaValue]);

  function handlePaginationChange(direction: 'prev' | 'next') {
    const cursor =
      direction === 'next' ? (data?.next ? data.next : '') : data?.prev ? data.prev : '';

    navigate({
      search: (prev) => ({
        ...prev,
        direction,
        cursor,
      }),
      params: true,
    });
  }

  const handlePageSizeChange = (pageSize: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        pageSize,
        cursor: '',
        direction: 'next',
      }),
      params: true,
    });
  };

  const handleWithEmailChange = (withEmail: boolean) => {
    navigate({
      search: (prev) => ({
        ...prev,
        withEmail,
        cursor: '',
        direction: 'next',
      }),
      params: true,
    });
  };

  const handleMetaFilterSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!canApplyMetaFilter) {
      return;
    }

    navigate({
      search: (prev) => ({
        ...prev,
        metaKey: metaKey.trim(),
        metaOperator,
        metaValue: metaOperator === 'exists' ? '' : metaValue.trim(),
        cursor: '',
        direction: 'next',
      }),
      params: true,
    });
  };

  const handleMetaFilterClear = () => {
    setMetaKey('');
    setMetaOperator('contains');
    setMetaValue('');
    navigate({
      search: (prev) => ({
        ...prev,
        metaKey: '',
        metaOperator: 'contains',
        metaValue: '',
        cursor: '',
        direction: 'next',
      }),
      params: true,
    });
  };

  const usersData = isFreePlan ? ({ data: mockData } as GetUsersResponseDto) : data || { data: [] };
  const emptyTitle = hasMetaFilter
    ? 'No users match this metadata filter'
    : search.withEmail
      ? 'No users with an email'
      : 'There are no users yet';
  const emptyDescription = hasMetaFilter
    ? search.metaOperator === 'exists'
      ? `No users have the "${search.metaKey}" metadata field.`
      : `No users have "${search.metaKey}" ${search.metaOperator === 'eq' ? 'equal to' : 'containing'} "${search.metaValue}".`
    : search.withEmail
      ? 'No users in this project have an email address.'
      : 'Someone should submit feature request from integration first';

  return (
    <PageContainer className="flex flex-col py-4 pb-0 h-[calc(100vh-80px)]">
      <PageHeader
        className="flex-shrink-0"
        title="Users"
        rightControls={
          isFreePlan ? (
            <UpgradeLink />
          ) : (
            <div className="flex items-center gap-2">
              <Switch
                id="users-with-email"
                checked={search.withEmail}
                onCheckedChange={handleWithEmailChange}
              />
              <Label className="cursor-pointer" htmlFor="users-with-email">
                With email
              </Label>
            </div>
          )
        }
      />
      {!isFreePlan ? (
        <form className="mb-4 flex flex-wrap items-center gap-2" onSubmit={handleMetaFilterSubmit}>
          <Label className="sr-only" htmlFor="users-meta-key">
            Metadata field
          </Label>
          <Input
            className="w-44"
            id="users-meta-key"
            maxLength={100}
            onChange={(event) => setMetaKey(event.target.value)}
            placeholder="Metadata field"
            value={metaKey}
          />
          <Select
            onValueChange={(value) => setMetaOperator(value as MetaOperator)}
            value={metaOperator}
          >
            <SelectTrigger aria-label="Metadata operator" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="eq">is</SelectItem>
              <SelectItem value="contains">contains</SelectItem>
              <SelectItem value="exists">exists</SelectItem>
            </SelectContent>
          </Select>
          {metaOperator !== 'exists' ? (
            <>
              <Label className="sr-only" htmlFor="users-meta-value">
                Metadata value
              </Label>
              <Input
                className="w-52"
                id="users-meta-value"
                maxLength={500}
                onChange={(event) => setMetaValue(event.target.value)}
                placeholder="Metadata value"
                value={metaValue}
              />
            </>
          ) : null}
          <Button disabled={!canApplyMetaFilter} size="sm" type="submit">
            Apply filter
          </Button>
          {hasMetaFilter ? (
            <Button
              aria-label="Clear metadata filter"
              onClick={handleMetaFilterClear}
              size="icon"
              type="button"
              variant="ghost"
            >
              <XIcon />
            </Button>
          ) : null}
        </form>
      ) : null}
      {usersData?.data.length ? (
        <div className="flex flex-col flex-1 min-h-0">
          <UsersDataTable
            data={usersData}
            metaKey={search.metaKey || undefined}
            pageSize={search.pageSize}
            onPageSizeChange={handlePageSizeChange}
            onPaginationChange={handlePaginationChange}
          />
          {isFreePlan ? (
            <ProTierPreview className="-mx-6 -my-4 h-[calc(100%+32px)]! w-[calc(100%+32px)]! sm:-mx-4" />
          ) : null}
        </div>
      ) : (
        <EmptyPlaceholder
          Icon={hasMetaFilter ? SearchIcon : UsersIcon}
          title={emptyTitle}
          description={emptyDescription}
        />
      )}
    </PageContainer>
  );
}
